import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { CombinedPriceInfoStore } from 'entities/Billing'
import { InboxesApi } from 'entities/Inbox'
import { SendFromMultiStore } from 'widgets/SendFromMulti'
import type { IAssignFlow } from './flows'

export class AssignExistingInboxStore {
  private _loading = false
  private _flow: IAssignFlow | null = null
  sendFromMultiStore: SendFromMultiStore
  readonly combinedPriceInfoStore: CombinedPriceInfoStore

  constructor(readonly entities: IEntitiesStore) {
    this.sendFromMultiStore = new SendFromMultiStore(this.entities, {
      getDropdownItemsOptions: {
        hideChildren: true,
        disabledClosedTooltip: true,
      },
      isInboxMode: true,
    })

    this.combinedPriceInfoStore = new CombinedPriceInfoStore({
      entities: this.entities,
    })

    makeAutoObservable(this)
  }

  get selectedInboxIds() {
    return this.sendFromMultiStore.selectedInboxIds
  }

  get loading() {
    return this._loading
  }

  get canAssign() {
    return this.selectedInboxIds.length > 0 && !this._loading
  }

  get isLoadingSeatPrice() {
    return this.combinedPriceInfoStore.isLoading
  }

  setFlow = (flow: IAssignFlow) => {
    this._flow = flow
  }

  handleAssign = async (): Promise<boolean> => {
    if (this.selectedInboxIds.length === 0 || !this._flow) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      const result = await this._flow.handleExistingInboxAssign(this.selectedInboxIds)

      if (result.success) {
        const inboxCount = this.selectedInboxIds.length
        toastStore.add({
          type: 'success',
          title: this._flow.getExistingInboxSuccessToast(inboxCount),
        })
        this.sendFromMultiStore.selectedDropdownItemsMap.clear()
      }

      return result.success
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to add member to inbox. Please try again.',
      })
      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  loadSeatPriceData = () => {
    this.combinedPriceInfoStore.updatePriceData(0, 1)
  }

  filterAvailableInboxes = async (memberInboxIds: number[] = []) => {
    this.sendFromMultiStore.selectedDropdownItemsMap.clear()
    await this.sendFromMultiStore.initPromise

    let excludeIds = [...memberInboxIds]

    const isManageInboxesAccess = this.entities.userPermissionsStore.getItem(
      'can_manage_organization_inboxes'
    )

    if (!isManageInboxesAccess) {
      const currentUserId = this.entities.usersStore.user?.id
      const inboxIdsWhereNotOwner = this.entities.inboxesStore.organizationInboxes
        .filter((inbox) => inbox.ownerId !== currentUserId)
        .map((inbox) => inbox.id)
      excludeIds = [...excludeIds, ...inboxIdsWhereNotOwner]
    }

    this.sendFromMultiStore.setExcludeInboxIds(excludeIds)
  }

  addMembersToExistingInbox = async (memberId: number, inboxIds: number[]): Promise<boolean> => {
    await InboxesApi.addMembersToExistingInbox(memberId, {
      teams: inboxIds.map((id) => ({ id })),
    })
    return true
  }

  reset = () => {
    this._loading = false
    this.sendFromMultiStore.selectedDropdownItemsMap.clear()
    this.sendFromMultiStore.setExcludeInboxIds([])
    this.combinedPriceInfoStore.reset()
  }
}
